import useRequest from '@/renderer/utils/useRequest';
import type { RequestSchema } from '@/types/request';
import { useMemo } from 'react';

import type { ResultsFilters } from './FilterTypes';
import { resolveSportIds } from './consts';

type ChartResponse = API.MembershipProductStats & {
  chart?: { date: string; units: string | number }[];
};

const playTypeFilter = (playType?: ResultsFilters['playType']) =>
  playType === 'full'
    ? ['side', 'total', 'prop', 'future', 'not-available']
    : playType === 'hp'
      ? ['side', 'total', 'not-available']
      : playType === 'st'
        ? ['side', 'total']
        : playType === 'pf'
          ? ['prop', 'future']
          : playType && typeof playType === 'string'
            ? [playType]
            : playType || ['side', 'total', 'prop', 'future', 'not-available'];

export type ResultsSummary = {
  wins: number;
  losses: number;
  pushes: number;
  units: number;
  isLoading: boolean;
};

/**
 * The headline record + units for the header card (#2004). Archive reads the public
 * `/release/stats` rollup; the member reads `/messageReceipt/stats/me`. Both share the
 * request keys/cache with the breakdown + chart requests, so this adds no extra fetch.
 */
export function useResultsSummary(
  userId: API.Id | 'me' | 'archive',
  filters: ResultsFilters,
  // Hold both requests while the caller is still deciding which scope to show, so the
  // scope it settles on is the only one fetched (#2142).
  { paused = false }: { paused?: boolean } = {},
): ResultsSummary {
  const isArchive = userId === 'archive';
  const { sportId: sportIdBase, startDate, endDate, playType, free, gameType } = filters;
  const sportId = useMemo(() => resolveSportIds(sportIdBase), [sportIdBase]);
  const serviceIds = useMemo(() => (sportIdBase === 'plus' ? [46, 59] : undefined), [sportIdBase]);

  const chartSchema = useMemo<RequestSchema<ChartResponse>>(
    () => ({ name: 'getChartRequest', input: `/api/v1/messageReceipt/stats/${userId}` }),
    [userId],
  );
  const chartRequest = useRequest(
    chartSchema,
    useMemo(
      () => ({
        cache: { maxAge: 90 * 1000 },
        clientOnly: !isArchive,
        query: {
          chart: true,
          count: true,
          tz:
            (typeof Intl !== 'undefined' &&
              Intl?.DateTimeFormat?.()?.resolvedOptions?.()?.timeZone) ||
            undefined,
          playType: playTypeFilter(playType),
          [Array.isArray(sportId) ? 'sportId$in' : 'sportId']: sportId,
          'message.serviceIds': serviceIds,
          startDate,
          endDate,
          free,
          gameType,
          groupBy: isArchive ? 'playType' : undefined,
        },
        // The member reads the record from this endpoint; archive uses statsRequest.
        pause: isArchive || paused,
      }),
      [isArchive, paused, serviceIds, sportId, playType, startDate, endDate, free, gameType],
    ),
  );

  const statsSchema = useMemo<RequestSchema<API.ReleaseStats>>(
    () => ({ name: 'getStatsRequest', input: '/api/v1/release/stats' }),
    [],
  );
  const statsRequest = useRequest(
    statsSchema,
    useMemo(
      () => ({
        cache: { maxAge: 90 * 1000 },
        clientOnly: false,
        pause: !isArchive || paused,
        query: {
          data: {
            playType: playTypeFilter(playType),
            [Array.isArray(sportId) ? 'sportId$in' : 'sportId']: sportId,
            'message.serviceIds': serviceIds,
            'gameTime$>=': startDate,
            'gameTime$<=': endDate,
            free,
            gameType,
          },
        },
      }),
      [isArchive, paused, serviceIds, sportId, playType, startDate, endDate, free, gameType],
    ),
  );

  if (isArchive) {
    const s = statsRequest.data;
    return {
      wins: Number(s?.result?.win?.total || 0),
      losses: Number(s?.result?.loss?.total || 0),
      pushes: Number(s?.result?.push?.total || 0),
      units: Number(s?.result?.win?.units || 0) + Number(s?.result?.loss?.units || 0),
      isLoading: statsRequest.loading || s === undefined,
    };
  }

  const c = chartRequest.data;
  return {
    wins: Number(c?.win || 0),
    losses: Number(c?.loss || 0),
    pushes: Number(c?.push || 0),
    units: Number(c?.units || 0),
    isLoading: chartRequest.loading || c === undefined,
  };
}
